import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountByIdStore, $currencyStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { AccountType } from '@shared/api/rest/accounts';
import { AccountSidebar, ManagersBlock } from './widgets';
import { AccountTileBig } from '@pages';
import { CardsTable, Details, SidebarSection, List as Transactions } from '@widgets';
import { NoData, PageContainer, Text } from '@shared/widgets';

export const AccountPage = () => {
  const params = useParams();
  const id = useMemo(() => (params.id ? parseInt(params.id) : undefined), [params]);

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const accountDetailsStore = useUnit($accountByIdStore.store);
  const accountStore = useUnit($initialAccountsStore.store);

  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(id);

  const { t } = useTranslation('account');

  useEffect(() => {
    $currencyStore.events.update();
  }, []);

  useEffect(() => {
    if (!params.id) return;
    $accountByIdStore.events.get(selectedAccountId);
    return () => $accountByIdStore.events.clear();
  }, [selectedAccountId]);

  const mainAccount = useMemo(() => {
    if (!accountDetailsStore.data || !accountStore.data) return undefined;
    const { mainAccId } = accountDetailsStore.data;
    return mainAccId === null ? accountDetailsStore.data : accountStore.data.find((a) => a.id === mainAccId);
  }, [accountDetailsStore.data, accountStore.data]);
  const subAccounts = useMemo(() => {
    if (!mainAccount) return accountStore.data.filter((a) => a.mainAccId != null);
    return accountStore.data.filter((a) => a.mainAccId === mainAccount.id);
  }, [mainAccount, accountStore.data]);
  const accountIds = useMemo(() => [selectedAccountId!], [selectedAccountId]);
  return (
    <PageContainer loading={accountDetailsStore.loading} title={t('title')} back backText={t('backText')}>
      {!accountDetailsStore.data && <NoData />}
      {accountDetailsStore.data && (
        <main className={cn('flex w-full flex-row gap-6', 'max-[1440px]:flex-col')}>
          <div
            className={cn(
              'flex flex-col gap-6',
              accountDetailsStore.data.type === AccountType.Card && 'w-full',
              accountDetailsStore.data.type === AccountType.Payment && ['w-[calc(100%-18em)]', 'max-[1440px]:w-full'],
            )}
          >
            <AccountTileBig
              account={accountDetailsStore.data}
              mainAccount={mainAccount}
              subAccounts={subAccounts}
              selectedAccountId={selectedAccountId}
              onAccountSwitch={(accountId) => setSelectedAccountId(accountId)}
            />
            {profileFeatureStore.data?.userManagement?.viewUsers && <ManagersBlock accountId={selectedAccountId!} />}
            <section
              className={cn(
                PaletteUtils.isVcfcPalette && 'mb-6 rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
              )}
            >
              <CardsTable title={t('card.title')} listType={false} accountId={selectedAccountId} />
            </section>
            {accountDetailsStore?.data && (
              <section className={cn('flex flex-col gap-6', PaletteUtils.isVcfcPalette && 'bg-white p-6 dark:bg-soft-black')}>
                {PaletteUtils.isInsigniaPalette && (
                  <Text weight='semibold' size='2xl' className='mb-0 pb-0'>
                    {t('tables.transaction.title', { ns: 'common' })}
                  </Text>
                )}
                <Transactions accountType={accountDetailsStore.data.type} accountIds={accountIds} filter search />
              </section>
            )}
          </div>
          <aside className='flex h-fit min-w-72'>
            {accountDetailsStore.data.type === AccountType.Card && <AccountSidebar />}
            {accountDetailsStore.data.type === AccountType.Payment && (
              <SidebarSection caption={t('sidebar.accountDetails.title')}>
                <Details />
              </SidebarSection>
            )}
          </aside>
        </main>
      )}
    </PageContainer>
  );
};
